// frontend/src/pages/ActivityUpload.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import { api } from "api";
import ActivityFormFields from "../components/ActivityFormFields";
import ActivityCard from "../components/ActivityCard";
import { extractMentionIds } from "../utils/mentions";
import { Preferences } from "@capacitor/preferences"; 

import { X } from "phosphor-react";

// Robust user id resolver (Preferences -> localStorage -> /users/me)
const getUserId = async () => {
  try {
    const r = await Preferences.get({ key: "user_id" });
    if (r?.value) return Number(r.value);
  } catch {}

  if (typeof window !== "undefined") {
    const v = localStorage.getItem("user_id");
    if (v) return Number(v);
  }

  // final fallback: ask the API (axios interceptor adds auth)
  try {
    const { data } = await api.get("/users/me");
    if (data?.id) {
      try {
        await Preferences.set({ key: "user_id", value: String(data.id) });
      } catch {}
      if (typeof window !== "undefined") {
        localStorage.setItem("user_id", String(data.id));
      }
      return Number(data.id);
    }
  } catch {}

  return null;
};

const MAX_MEDIA = 5;

const getLocalDatetimeValue = () => {
  const date = new Date();
  const pad = (value) => String(value).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const ActivityUpload = ({
  circleId = null,
  circleName = null,
  fetchFeed,
  /** optional: when success popup is dismissed, close parent (desktop modal) */
  onClose,
}) => {
    const [formData, setFormData] = useState(() => ({
    title: "",
    description: "",
    tags: "",
    mood: "",
    duration: "",
    location: "",
    media_file: null,
    media_files: [],
    timestamp: getLocalDatetimeValue(),
    circle_ids: [],
    visibility: "",
    base_visibility: "",
  }));

  const [joinedCircles, setJoinedCircles] = useState([]);
  const [preview, setPreview] = useState(false);
  const [error, setError] = useState("");
  const [successPopup, setSuccessPopup] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [resolvedCircleName, setResolvedCircleName] = useState(circleName);
  const [resetKey, setResetKey] = useState(0); // force remount to clear file input
  const [me, setMe] = useState(null); // { id, name, username, profile_pic_url }
  const rootRef = useRef(null); // for collapsing parent <details>

  // Load current user (for preview author label)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/me");
        if (data?.id) setMe(data);
      } catch {
        setMe(null);
      }
    })();
  }, []);

  // Load user's joined circles (for multi-select)
  useEffect(() => {
    let cancelled = false;

    const fetchJoined = async () => {
      try {
        const userId = await getUserId();
        if (!userId) return;

        const res = await api.get(`/users/${userId}/groups`);
        if (!cancelled) setJoinedCircles(res.data || []);
      } catch (err) {
        console.error("Failed to fetch joined circles:", err);
      }
    };

    fetchJoined();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!circleId) return;

    setFormData((current) => {
      const ids = new Set([
        ...(current.circle_ids || []),
        Number(circleId),
      ]);

      return {
        ...current,
        circle_ids: Array.from(ids),
      };
    });
  }, [circleId]);

  // If we only have an ID, fetch the circle name once
  useEffect(() => {
    if (circleId && !circleName) {
      (async () => {
        try {
          const r = await api.get(`/groups/${circleId}`);
          setResolvedCircleName(r.data?.name ?? null);
        } catch {
          setResolvedCircleName(null);
        }
      })();
    }
  }, [circleId, circleName]);

  // Revoke single preview URL on unmount (avoid leaks on iOS/WebKit)
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handlePreview = (e) => {
    e?.preventDefault?.();
    setPreview(true);
  };

  const collapseParentDetails = () => {
    const host = rootRef.current;
    const details = host?.closest?.("details");
    if (details && details.hasAttribute("open")) {
      details.removeAttribute("open");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      tags: "",
      mood: "",
      duration: "",
      location: "",
      media_file: null,
      media_files: [],
      timestamp: getLocalDatetimeValue(),
      circle_ids: [],
      visibility: "",
      base_visibility: "",
    });
    setPreview(false);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setResetKey((k) => k + 1); // clears native file input
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const tagList = formData.tags
      ? formData.tags.split(",").map((t) => t.trim()).filter(Boolean)
      : [];

    const uploadData = new FormData();
    uploadData.append("title", String(formData.title));
    if (formData.description) uploadData.append("description", String(formData.description));
    const mentionIds = extractMentionIds(String(formData.description || ""));
    if (mentionIds.length) uploadData.append("mentions", JSON.stringify(mentionIds));
    if (formData.mood) uploadData.append("mood", String(formData.mood));
    if (formData.duration) uploadData.append("duration", String(formData.duration));
    if (formData.location) uploadData.append("location", String(formData.location));
    if (formData.timestamp) {
      uploadData.append("timestamp", new Date(formData.timestamp).toISOString());
    }
    if (tagList.length) uploadData.append("tags", JSON.stringify(tagList));

    const normalizedCircleId =
      circleId != null ? Number(circleId) : null;

    const circleIdsPayload = Array.from(
      new Set(
        (formData.circle_ids || [])
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0)
      )
    );

    if (circleIdsPayload.length) {
      uploadData.append(
        "circle_ids",
        JSON.stringify(circleIdsPayload)
      );
    }
  
    // Activity-level privacy: only send when explicitly private.
    if (formData.visibility) {
      uploadData.append("visibility", formData.visibility);
    } else {
      uploadData.append("visibility", ""); // explicit default
    }
    
    // Collect files (array or single)
    const files = Array.isArray(formData.media_files) && formData.media_files.length
      ? formData.media_files
      : (formData.media_file ? [formData.media_file] : []);
    const limited = files.slice(0, MAX_MEDIA);

    limited.forEach((file) => {
      uploadData.append("media_files", file);
    });

    try {
      const { data } = await api.post("/activities/upload", uploadData);

      // notify others (ProfileCard, etc.) immediately
      const userId = await getUserId();
      const createdAt = new Date().toISOString();
      const tsISO = formData.timestamp
        ? new Date(formData.timestamp).toISOString()
        : createdAt;

      window.dispatchEvent(
        new CustomEvent("activity:created", {
          detail: {
            activity: {
              id: data?.id,
              user_id: userId,
              title: formData.title,
              description: formData.description,
              tags: tagList,
              mood: formData.mood,
              duration: formData.duration,
              location: formData.location,
              created_at: createdAt,
              timestamp: tsISO,
              visibility: formData.visibility || null,
              is_private: formData.visibility === "private",
            },
          },
        })
      );

      if (fetchFeed) fetchFeed();

      setSuccessPopup(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      console.error("Upload failed:", detail || err);
      setError(
        Array.isArray(detail)
          ? detail.map((d) => `${d.loc?.join(".")}: ${d.msg}`).join(" • ")
          : detail || (err?.response?.status === 401 ? "Not authenticated" : "Failed to upload activity")
      );
    }
  };

  // Called when user dismisses the success popup
  const handleSuccessDismiss = () => {
    setSuccessPopup(false);
    resetForm();
    collapseParentDetails();
    // if embedded in desktop modal, close it after success
    try { onClose?.(); } catch {}
  };

  const selectedCircleIds = useMemo(() => {
    return Array.from(
      new Set(
        (formData.circle_ids || [])
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0)
      )
    );
  }, [formData.circle_ids]);

  const selectedCircleNames = useMemo(() => {
    const idToName = new Map(
      (joinedCircles || []).map((circle) => {
        const id = Number(
          circle.id ??
          circle.group_id ??
          circle.circle_id
        );

        const name =
          circle.name ??
          circle.title ??
          circle.handle ??
          `Circle #${id}`;

        return [id, name];
      })
    );

    return selectedCircleIds.map((id) => {
      if (Number(id) === Number(circleId) && resolvedCircleName) {
        return resolvedCircleName;
      }

      return idToName.get(id) || `Circle #${id}`;
    });
  }, [
    selectedCircleIds,
    joinedCircles,
    circleId,
    resolvedCircleName,
  ]);

  const showPostingMessage = selectedCircleNames.length > 0;

  // Build preview object
  const previewActivity = useMemo(() => {
    if (!preview) return null;

    const tagList = formData.tags
      ? formData.tags.split(",").map((t) => t.trim()).filter(Boolean)
      : [];

    const normalizedCircleId =
      circleId != null ? Number(circleId) : null;

    const ids = Array.from(
      new Set(
        (formData.circle_ids || [])
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0)
      )
    );

    const idToName = new Map(
      (joinedCircles || []).map((g) => [
        String(g.id ?? g.group_id ?? g.circle_id),
        g.name ?? g.title ?? g.handle ?? `Circle #${g.id}`,
      ])
    );
    const embeddedCircles = ids.map((id) => ({
      id,
      name: idToName.get(String(id)) || `Circle #${id}`,
    }));

    const tsISO = formData.timestamp
      ? new Date(formData.timestamp).toISOString()
      : new Date().toISOString();

    const author = me
      ? { id: me.id, name: me.name || me.username || "You", username: me.username, profile_pic_url: me.profile_pic_url }
      : { id: null, name: "You" };

    // Collect files (array or single)
    const files = Array.isArray(formData.media_files) && formData.media_files.length
      ? formData.media_files
      : (formData.media_file ? [formData.media_file] : []);
    const limited = files.slice(0, MAX_MEDIA);

    // Build preview blobs + types
    const media_urls = limited.map((f) => URL.createObjectURL(f));
    const _media_types = limited.map((f) => f.type || null);

    // Respect single previewUrl when provided and no files captured above
    if (!media_urls.length && previewUrl) {
      media_urls.push(previewUrl);
      _media_types.push(formData.media_file?.type || null);
    }


    return {
      id: -1,
      user: author,
      user_id: author.id,
      title: formData.title || "",
      description: formData.description || "",
      tags: tagList,
      mood: formData.mood || "",
      duration: formData.duration || "",
      location: formData.location || "",
      timestamp: tsISO,
      created_at: tsISO,

      // gallery fields for ActivityCard
      media_urls,
      _media_types,

      // back-compat single fields (first one)
      media_url: media_urls[0] || null,
      _media_type: _media_types[0] || null,

      circle_ids: ids,
      circles: embeddedCircles,
      high_five_count: 0,
      comment_count: 0,
      has_high_fived: false,
      highfivers_preview: [],
      commenters_preview: [],
      visibility: formData.visibility || null,
      is_private: formData.visibility === "private",
    };
  }, [preview, formData, previewUrl, joinedCircles, circleId, me]);

  // Revoke preview-created object URLs whenever preview changes/closes or data changes
  const lastPreviewUrlsRef = useRef([]);
  useEffect(() => {
    lastPreviewUrlsRef.current.forEach((u) => {
      try { if (u?.startsWith("blob:")) URL.revokeObjectURL(u); } catch {}
    });
    lastPreviewUrlsRef.current = [];

    if (preview && previewActivity?.media_urls?.length) {
      lastPreviewUrlsRef.current = [...previewActivity.media_urls];
    }

    return () => {
      lastPreviewUrlsRef.current.forEach((u) => {
        try { if (u?.startsWith("blob:")) URL.revokeObjectURL(u); } catch {}
      });
      lastPreviewUrlsRef.current = [];
    };
  }, [preview, previewActivity]);

  const hasTitle = formData.title.trim().length > 0;

  return (
    <div
      ref={rootRef}
      className="max-w-[91%] mx-auto pb-6 space-y-3 !h-auto !min-h-0"
    >
      {showPostingMessage && (
        <p className="mb-4 ml-3 text-sm text-gray-500">
          You're posting this to{" "}
          <span className="font-semibold">
            {selectedCircleNames.join(", ")}
          </span>
        </p>
      )}

      <form className="space-y-2" onSubmit={handlePreview}>
        <ActivityFormFields
          key={resetKey}
          form={formData}
          setForm={setFormData}
          circleId={null}
          joinedCircles={joinedCircles}
          showCircles={true}
          showTimestamp={true}
          previewUrl={previewUrl}
          setPreviewUrl={setPreviewUrl}
        />

        {error && <p className="text-red-600">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handlePreview}
            disabled={!hasTitle}
            className={`
              rounded-full text-sm !min-h-0 !min-w-0 px-4 py-2
              ${
                hasTitle
                  ? "bg-white text-black hover:bg-blue-300"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }
            `}
          >
            Preview
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!hasTitle}
            className={`
              rounded-3xl text-sm !min-h-0 !min-w-0 px-4 py-2
              ${
                hasTitle
                  ? "bg-blue-500 text-white hover:bg-blue-600"
                  : "bg-blue-300 text-white opacity-60 cursor-not-allowed"
              }
            `}
          >
            Submit
          </button>
        </div>
      </form>

      {/* Read-only activity preview lightbox */}
      {preview && previewActivity && (
        <div className="fixed inset-0 z-[200] grid place-items-center bg-black/40 px-3">
          <div className="bg-white rounded-3xl w-full pb-2 max-w-[380px] sm:max-w-[535px] shadow-[0_8px_0px_rgba(37,99,235,0.96)] overflow-hidden">
            {/* Fixed close button */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setPreview(false)}
                className="grid place-items-center !min-h-0 !min-w-0 !h-9 !w-9 mt-3 mr-3 text-gray-700 transition"
                aria-label="Close preview"
              >
                <X
                  size={16}
                  className="sm:!w-5 sm:!h-5"
                  weight="bold"
                />
              </button>
            </div>

            {/* Scrollable preview body */}
            <div 
              className="
                w-full max-w-xl
                max-h-[90vh]
                overflow-y-auto
                rounded-3xl bg-white
              ">
              <div className="px-2.5 sm:px-3 pb-2">
                <ActivityCard
                  activity={previewActivity}
                  currentUser={me || null}
                  onTagClick={() => {}}
                  readOnly={true}
                  contentClassName="px-4 pb-3 sm:pt-21"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success overlay */}
      {successPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-xl text-center space-y-3">
            <h2 className="text-[1.1rem] font-semibold">
              Activity uploaded successfully!
            </h2>
            <button
              onClick={handleSuccessDismiss}
              className="bg-blue-600 text-white px-4 py-2 rounded"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityUpload;
